import { createContext, useContext, type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";

import { getEntities, getSettings, type BusinessSettings } from "src/api/settings";
import { useAuth } from "src/providers/AuthProvider";
import type { Entity } from "src/types/inventory";

export type { BusinessSettings };

interface DashboardContextValue {
	entities: Entity[];
	settings: BusinessSettings | null;
}

const Context = createContext<DashboardContextValue>({ entities: [], settings: null });

const DashboardContext = ({ children }: { children: ReactNode }) => {
	const { user } = useAuth();

	const [{ data: rawEntities }, { data: rawSettings }] = useQueries({
		queries: [
			{ queryKey: ["entities"], queryFn: () => getEntities({ limit: 30 }), suspense: true },
			{ queryKey: ["settings"], queryFn: () => getSettings({ businessUuid: user?.business_uuid }), suspense: true },
		],
	});

	const entities = rawEntities?.data?.data ?? [];
	const settings = rawSettings?.data ?? null;

	return <Context.Provider value={{ entities, settings }}>{children}</Context.Provider>;
};

export const useDashboardContext = () => {
	const context = useContext(Context);

	if (!context) throw new Error("Use dashboard context within provider");

	return context;
};

export default DashboardContext;
